import clsx from "clsx";
import React from "react";

import { isDarwin } from "@excalidraw/common";

import { getShortcutFromShortcutName } from "../actions/shortcuts";
import { t } from "../i18n";

import { useExcalidrawAppState, useExcalidrawElements } from "./App";

import { Popover } from "./Popover";

import "./ContextMenu.scss";

import type { ActionManager } from "../actions/manager";
import type { ShortcutName } from "../actions/shortcuts";
import type { Action } from "../actions/types";

import type { TranslationKeys } from "../i18n";

export type ContextMenuItem = typeof CONTEXT_MENU_SEPARATOR | Action;

export type ContextMenuItems = (ContextMenuItem | false | null | undefined)[];

type ContextMenuProps = {
  actionManager: ActionManager;
  items: ContextMenuItems;
  top: number;
  left: number;
  onClose: (callback?: () => void) => void;
};

export const CONTEXT_MENU_SEPARATOR = "separator";

// apple-style quick-action strip at the top of the menu — these render as
// icon tiles in one row instead of full-width text rows. Order here is the
// order they appear in the strip.
const QUICK_ACTIONS = ["cut", "copy", "duplicateSelection", "paste"];

// figma-style shortcut rendering: mac gets glyphs (⇧⌘C), others keep "Ctrl+C"
const formatShortcut = (shortcut: string) => {
  if (!isDarwin || !shortcut) {
    return shortcut;
  }
  return (
    shortcut
      .replace(/Shift/gi, "⇧")
      .replace(/Option|Alt/gi, "⌥")
      .replace(/Cmd|Command/gi, "⌘")
      .replace(/Ctrl/gi, "⌃")
      .replace(/Delete/gi, "⌫")
      // drop only separator "+" (followed by another char); keep a trailing
      // literal "+" key, e.g. zoom-in "⌘++" must render "⌘+" not "⌘"
      .replace(/\+(?=.)/g, "")
      .replace(/\s+/g, " ")
  );
};

export const ContextMenu = React.memo(
  ({ actionManager, items, top, left, onClose }: ContextMenuProps) => {
    const appState = useExcalidrawAppState();
    const elements = useExcalidrawElements();

    const filteredItems = items.reduce((acc: ContextMenuItem[], item) => {
      if (
        item &&
        (item === CONTEXT_MENU_SEPARATOR ||
          !item.predicate ||
          item.predicate(
            elements,
            appState,
            actionManager.app.props,
            actionManager.app,
          ))
      ) {
        acc.push(item);
      }
      return acc;
    }, []);

    const getLabel = (item: Action) => {
      if (!item.label) {
        return "";
      }
      return t(
        (typeof item.label === "function"
          ? item.label(elements, appState, actionManager.app)
          : item.label) as unknown as TranslationKeys,
      );
    };

    const getIcon = (item: Action) =>
      typeof item.icon === "function"
        ? item.icon(appState, elements)
        : item.icon;

    const run = (item: Action) => {
      // we need update state before executing the action in case
      // the action uses the appState it's being passed (that still
      // contains a defined contextMenu) to return the next state.
      onClose(() => {
        actionManager.executeAction(item, "contextMenu");
      });
    };

    const quickCandidates = QUICK_ACTIONS.map((name) =>
      filteredItems.find(
        (item): item is Action =>
          item !== CONTEXT_MENU_SEPARATOR && item.name === name,
      ),
    ).filter((item): item is Action => !!item);

    // a strip holding a single tile (the canvas menu, which only has Paste)
    // wastes a whole row — leave it as a normal text row instead
    const quickItems = quickCandidates.length > 1 ? quickCandidates : [];

    const restItems = filteredItems.filter(
      (item) => !quickItems.includes(item as Action),
    );

    return (
      <Popover
        onCloseRequest={() => {
          onClose();
        }}
        top={top}
        left={left}
        fitInViewport={true}
        offsetLeft={appState.offsetLeft}
        offsetTop={appState.offsetTop}
        viewportWidth={appState.width}
        viewportHeight={appState.height}
        className="context-menu-popover"
      >
        <ul
          className="context-menu"
          onContextMenu={(event) => event.preventDefault()}
        >
          {quickItems.map((item) => (
            <li
              key={item.name}
              className="context-menu-quick"
              data-testid={item.name}
              onClick={() => run(item)}
            >
              <button type="button" className="context-menu-quick__button">
                <div className="context-menu-quick__icon">{getIcon(item)}</div>
                <div className="context-menu-quick__label">
                  {getLabel(item)}
                </div>
              </button>
            </li>
          ))}
          {quickItems.length > 0 && restItems.length > 0 && (
            <hr className="context-menu-item-separator" />
          )}
          {restItems.map((item, idx) => {
            if (item === CONTEXT_MENU_SEPARATOR) {
              if (
                !restItems[idx - 1] ||
                restItems[idx - 1] === CONTEXT_MENU_SEPARATOR
              ) {
                return null;
              }
              return <hr key={idx} className="context-menu-item-separator" />;
            }

            const actionName = item.name;

            return (
              <li key={idx} data-testid={actionName} onClick={() => run(item)}>
                <button
                  type="button"
                  className={clsx("context-menu-item", {
                    dangerous: actionName === "deleteSelectedElements",
                    checkmark: item.checked?.(appState),
                  })}
                >
                  <div className="context-menu-item__icon">{getIcon(item)}</div>
                  <div className="context-menu-item__label">
                    {getLabel(item)}
                  </div>
                  <kbd className="context-menu-item__shortcut">
                    {actionName
                      ? formatShortcut(
                          getShortcutFromShortcutName(
                            actionName as ShortcutName,
                          ),
                        )
                      : ""}
                  </kbd>
                </button>
              </li>
            );
          })}
        </ul>
      </Popover>
    );
  },
);
